import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Mail, Phone, MapPin, Calendar, Stethoscope, FileText, MessageSquare,
  Lock, Loader2, Send, CheckCircle2, Circle, Trash2, Building2,
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n/I18nContext";
import { toast } from "sonner";
import CaseStagePipeline, { CASE_STAGES } from "@/components/CaseStagePipeline";
import ComplianceCard from "@/components/ComplianceCard";
import TasksList from "@/components/TasksList";
import TreatmentPlanDialog from "@/components/TreatmentPlanDialog";
import HealthScoreBadge from "@/components/HealthScoreBadge";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function TreatmentSummary({ items, t }) {
  if (!items.length) {
    return (
      <div className="card-soft p-6 text-sm text-[#5C6B68]" data-testid="att-tx-empty">
        {t("att.treatments.empty")}
      </div>
    );
  }
  // Group by facility name (fall back to physician name)
  const byFacility = items.reduce((acc, it) => {
    const key = (it.facility || it.physician_name || "Unspecified provider").trim();
    if (!acc[key]) acc[key] = [];
    acc[key].push(it);
    return acc;
  }, {});
  return (
    <div className="grid gap-3" data-testid="att-tx-summary">
      {Object.entries(byFacility).map(([facility, visits]) => (
        <div key={facility} className="card-soft p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="inline-flex items-center gap-2 font-display text-base font-bold text-[#11322A]">
                <Building2 className="h-4 w-4 text-[#E85D04]" />
                {facility}
              </div>
              {visits[0]?.physician_specialty && (
                <div className="mt-1 text-xs text-[#5C6B68]">{visits[0].physician_specialty}</div>
              )}
            </div>
            <div className="text-right">
              <div className="font-display text-2xl font-black text-[#E85D04]">{visits.length}</div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-[#5C6B68]">
                {t("att.treatments.visits")}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function AttorneyCaseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const t = useT();
  const { user } = useAuth();
  const [lead, setLead] = useState(null);
  const [treatments, setTreatments] = useState([]);
  const [notes, setNotes] = useState([]);
  const [history, setHistory] = useState([]);
  const [messages, setMessages] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [newMsg, setNewMsg] = useState("");
  const [posting, setPosting] = useState(false);
  const [savingStage, setSavingStage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showPlanDialog, setShowPlanDialog] = useState(false);
  const [complianceKey, setComplianceKey] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [l, tx, n, h, m, d] = await Promise.all([
        api.get(`/attorney/cases/${id}`),
        api.get(`/attorney/cases/${id}/treatments`),
        api.get(`/attorney/cases/${id}/notes`),
        api.get(`/attorney/cases/${id}/stage-history`),
        api.get(`/attorney/cases/${id}/messages`),
        api.get(`/attorney/cases/${id}/documents`),
      ]);
      setLead(l.data);
      setTreatments(tx.data);
      setNotes(n.data);
      setHistory(h.data);
      setMessages(m.data);
      setDocuments(d.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
      navigate("/attorney");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const updateStage = async (stage) => {
    if (!lead || stage === lead.case_stage) return;
    setSavingStage(true);
    try {
      const { data } = await api.patch(`/attorney/cases/${id}/stage`, { stage });
      setLead(data);
      toast.success(t("att.stage.updated", { stage: t(`stage.${stage}`) }));
      // refresh history
      const h = await api.get(`/attorney/cases/${id}/stage-history`);
      setHistory(h.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSavingStage(false);
    }
  };

  const submitNote = async (e) => {
    e.preventDefault();
    if (!newNote.trim()) return;
    setPosting(true);
    try {
      const { data } = await api.post(`/attorney/cases/${id}/notes`, { body: newNote.trim() });
      setNotes((p) => [data, ...p]);
      setNewNote("");
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setPosting(false);
    }
  };

  const deleteNote = async (noteId) => {
    if (!confirm(t("att.notes.confirm_delete"))) return;
    try {
      await api.delete(`/attorney/cases/${id}/notes/${noteId}`);
      setNotes((p) => p.filter((n) => n.id !== noteId));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const submitMessage = async (e) => {
    e.preventDefault();
    if (!newMsg.trim()) return;
    setPosting(true);
    try {
      const { data } = await api.post(`/attorney/cases/${id}/messages`, { body: newMsg.trim() });
      setMessages((p) => [...p, data]);
      setNewMsg("");
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setPosting(false);
    }
  };

  const toggleTreatmentReview = async (txId) => {
    try {
      const { data } = await api.post(`/attorney/cases/${id}/treatments/${txId}/review`);
      setTreatments((p) => p.map((t2) => (t2.id === txId ? data : t2)));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#11322A]" />
      </div>
    );
  }
  if (!lead) return null;

  const totalVisits = treatments.length;
  const facilities = new Set(treatments.map((tx) => (tx.facility || tx.physician_name || "").trim()).filter(Boolean));

  return (
    <div className="min-h-screen" data-testid="att-case-detail">
      <header className="glass-header sticky top-0 z-40">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between px-4 py-4 sm:px-6">
          <Link to="/attorney" className="inline-flex items-center gap-2 text-sm font-bold text-[#11322A] hover:text-[#E85D04]" data-testid="att-back">
            <ArrowLeft className="h-4 w-4" /> {t("att.case.back")}
          </Link>
          <div className="text-right text-xs">
            <div className="font-bold text-[#0B1317]">{user?.name}</div>
            <div className="text-[#5C6B68]">{user?.email}</div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-4 py-8 sm:px-6">
        {/* Client header */}
        <div className="card-soft p-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <span className="label-cap">{t("att.case.client")}</span>
              <h1 className="mt-1 font-display text-3xl font-black tracking-tight text-[#11322A] sm:text-4xl">
                {lead.full_name}
              </h1>
              <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-[#0B1317]">
                <a href={`mailto:${lead.email}`} className="inline-flex items-center gap-1.5 hover:text-[#E85D04]" data-testid="att-client-email">
                  <Mail className="h-3.5 w-3.5" />
                  {lead.email}
                </a>
                <a href={`tel:${lead.phone}`} className="inline-flex items-center gap-1.5 hover:text-[#E85D04]" data-testid="att-client-phone">
                  <Phone className="h-3.5 w-3.5" />
                  {lead.phone}
                </a>
                <span className="inline-flex items-center gap-1.5">
                  <MapPin className="h-3.5 w-3.5" />
                  {lead.state}
                  {lead.city ? ` · ${lead.city}` : ""}
                </span>
                {lead.accident_date && (
                  <span className="inline-flex items-center gap-1.5">
                    <Calendar className="h-3.5 w-3.5" />
                    {lead.accident_date}
                  </span>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 text-center">
              <div>
                <div className="font-display text-3xl font-black text-[#E85D04]">{totalVisits}</div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-[#5C6B68]">
                  {t("att.treatments.visits")}
                </div>
              </div>
              <div>
                <div className="font-display text-3xl font-black text-[#11322A]">{facilities.size}</div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-[#5C6B68]">
                  {t("att.treatments.facilities")}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Compliance + Tasks side-by-side */}
        <div className="mt-6 grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <ComplianceCard caseId={id} variant="attorney" refreshKey={complianceKey} />
            {!lead.treatment_plan_visits_per_week && (
              <button
                onClick={() => setShowPlanDialog(true)}
                className="btn-primary mt-3 text-sm"
                data-testid="att-set-plan"
              >
                <Stethoscope className="h-4 w-4" /> {t("att.plan.set_cta")}
              </button>
            )}
            {lead.treatment_plan_visits_per_week && (
              <button
                onClick={() => setShowPlanDialog(true)}
                className="btn-secondary mt-3 text-sm"
                data-testid="att-edit-plan"
              >
                {t("att.plan.edit_cta")}
              </button>
            )}
          </div>
          <div>
            <div className="label-cap mb-3">{t("att.tasks.heading")}</div>
            <TasksList caseId={id} scope="attorney" onChange={() => setComplianceKey((k) => k + 1)} />
          </div>
        </div>

        {/* Stage pipeline */}
        <div className="card-soft mt-6 p-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="label-cap">{t("att.stage.title")}</div>
              <h2 className="mt-1 font-display text-xl font-black text-[#11322A]">
                {t(`stage.${lead.case_stage || "intake"}`)}
              </h2>
              <p className="mt-1 text-xs text-[#5C6B68]">
                {t("att.stage.subtitle")} ·{" "}
                {lead.case_stage_updated_at ? fmtDate(lead.case_stage_updated_at) : "—"}
              </p>
            </div>
            {savingStage && <Loader2 className="h-4 w-4 animate-spin text-[#11322A]" />}
          </div>
          <div className="mt-5">
            <CaseStagePipeline current={lead.case_stage || "intake"} onSelect={updateStage} />
          </div>
        </div>

        {/* Tabs */}
        <div className="card-soft mt-6 p-6">
          <Tabs defaultValue="overview">
            <TabsList className="w-full justify-start" data-testid="att-tabs">
              <TabsTrigger value="overview" data-testid="att-tab-overview">{t("att.tab.overview")}</TabsTrigger>
              <TabsTrigger value="treatments" data-testid="att-tab-treatments">
                <Stethoscope className="mr-1 h-3.5 w-3.5" /> {t("att.tab.treatments")} ({totalVisits})
              </TabsTrigger>
              <TabsTrigger value="notes" data-testid="att-tab-notes">
                <Lock className="mr-1 h-3.5 w-3.5" /> {t("att.tab.notes")} ({notes.length})
              </TabsTrigger>
              <TabsTrigger value="messages" data-testid="att-tab-messages">
                <MessageSquare className="mr-1 h-3.5 w-3.5" /> {t("att.tab.messages")}
              </TabsTrigger>
              <TabsTrigger value="documents" data-testid="att-tab-documents">
                <FileText className="mr-1 h-3.5 w-3.5" /> {t("att.tab.documents")} ({documents.length})
              </TabsTrigger>
              <TabsTrigger value="history" data-testid="att-tab-history">{t("att.tab.history")}</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="mt-5 grid gap-4 sm:grid-cols-2 text-sm">
              <div className="sm:col-span-2">
                <div className="label-cap text-[10px]">{t("att.case.accident")}</div>
                <div className="mt-1 text-[#0B1317]">{lead.accident_type || "—"}</div>
              </div>
              <div>
                <div className="label-cap text-[10px]">{t("att.case.fault")}</div>
                <div className="mt-1">{lead.at_fault || "—"}</div>
              </div>
              <div>
                <div className="label-cap text-[10px]">{t("att.case.police_report")}</div>
                <div className="mt-1">{lead.police_report ? t("yes") : t("no")}</div>
              </div>
              <div className="sm:col-span-2">
                <div className="label-cap text-[10px]">{t("att.case.injuries")}</div>
                <div className="mt-1 whitespace-pre-wrap text-[#0B1317]">{lead.injuries || "—"}</div>
              </div>
              {lead.description && (
                <div className="sm:col-span-2">
                  <div className="label-cap text-[10px]">{t("att.case.notes_client")}</div>
                  <div className="mt-1 whitespace-pre-wrap text-[#0B1317]">{lead.description}</div>
                </div>
              )}
              {lead.temperature_reasoning && (
                <div className="sm:col-span-2 rounded-xl border border-[#E0E4E2] bg-[#FDFBF7] p-4">
                  <div className="label-cap text-[10px]">{t("att.case.ai_take")}</div>
                  <div className="mt-1 text-[#0B1317]">{lead.temperature_reasoning}</div>
                </div>
              )}
            </TabsContent>

            <TabsContent value="treatments" className="mt-5">
              <TreatmentSummary items={treatments} t={t} />
              {treatments.length > 0 && (
                <div className="mt-6">
                  <div className="label-cap">{t("att.treatments.detailed")}</div>
                  <ul className="mt-3 divide-y divide-[#E0E4E2] rounded-xl border border-[#E0E4E2] bg-white">
                    {treatments.map((tx) => {
                      const reviewed = (tx.reviewed_by_attorney_ids || []).length > 0;
                      return (
                        <li key={tx.id} className="grid grid-cols-1 items-center gap-3 p-4 sm:grid-cols-12">
                          <div className="sm:col-span-4">
                            <div className="font-display text-sm font-bold text-[#0B1317]">{tx.physician_name}</div>
                            {tx.facility && <div className="text-xs text-[#5C6B68]">{tx.facility}</div>}
                            {tx.physician_specialty && (
                              <div className="text-xs text-[#5C6B68]">{tx.physician_specialty}</div>
                            )}
                          </div>
                          <div className="sm:col-span-3 text-xs text-[#0B1317]">
                            <div className="font-bold">{tx.visit_type}</div>
                            <div className="text-[#5C6B68]">{tx.visit_date}</div>
                          </div>
                          <div className="sm:col-span-3 text-xs text-[#0B1317]">{tx.notes || "—"}</div>
                          <div className="sm:col-span-2 flex justify-end">
                            <button
                              onClick={() => toggleTreatmentReview(tx.id)}
                              className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-bold ${
                                reviewed
                                  ? "border-[#bce5cc] bg-[#E6F6ED] text-[#166534]"
                                  : "border-[#E0E4E2] bg-white text-[#5C6B68] hover:border-[#11322A] hover:text-[#11322A]"
                              }`}
                              data-testid={`att-tx-review-${tx.id}`}
                            >
                              {reviewed ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Circle className="h-3.5 w-3.5" />}
                              {reviewed ? t("att.treatments.reviewed") : t("att.treatments.mark_reviewed")}
                            </button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </TabsContent>

            <TabsContent value="notes" className="mt-5">
              <div className="rounded-xl border border-[#fcd9a3] bg-[#fff5e6] p-3 text-xs font-semibold text-[#B45309]">
                {t("att.notes.privacy")}
              </div>
              <form onSubmit={submitNote} className="mt-4 flex gap-2" data-testid="att-note-form">
                <textarea
                  rows={2}
                  className="input-field flex-1"
                  placeholder={t("att.notes.placeholder")}
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  data-testid="att-note-input"
                />
                <button
                  type="submit"
                  disabled={posting || !newNote.trim()}
                  className="btn-primary disabled:opacity-50"
                  data-testid="att-note-submit"
                >
                  {posting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </button>
              </form>
              <ul className="mt-4 space-y-3">
                {notes.length === 0 && (
                  <li className="card-soft p-4 text-sm text-[#5C6B68]" data-testid="att-notes-empty">
                    {t("att.notes.empty")}
                  </li>
                )}
                {notes.map((n) => (
                  <li key={n.id} className="card-soft p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-xs font-bold text-[#11322A]">{n.author_name}</div>
                        <div className="text-[10px] font-semibold uppercase tracking-wider text-[#5C6B68]">
                          {fmtDate(n.created_at)}
                        </div>
                      </div>
                      {n.author_user_id === user?.id && (
                        <button
                          onClick={() => deleteNote(n.id)}
                          className="text-[#B91C1C] hover:text-[#7f1d1d]"
                          data-testid={`att-note-delete-${n.id}`}
                          aria-label="Delete note"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                    <div className="mt-2 whitespace-pre-wrap text-sm text-[#0B1317]">{n.body}</div>
                  </li>
                ))}
              </ul>
            </TabsContent>

            <TabsContent value="messages" className="mt-5">
              <div className="rounded-xl border border-[#E0E4E2] bg-white" data-testid="att-msg-thread">
                <ul className="max-h-[420px] space-y-3 overflow-y-auto p-4">
                  {messages.length === 0 && (
                    <li className="text-sm text-[#5C6B68]">{t("att.messages.empty")}</li>
                  )}
                  {messages.map((m) => {
                    const mine = m.sender_role === "attorney";
                    return (
                      <li key={m.id} className={`flex gap-3 ${mine ? "flex-row-reverse" : ""}`}>
                        <div className={`max-w-[78%] rounded-2xl px-4 py-3 text-sm ${mine ? "bg-[#11322A] text-white" : "bg-[#FDFBF7] text-[#0B1317] border border-[#E0E4E2]"}`}>
                          <div className={`text-[10px] font-bold uppercase tracking-wider ${mine ? "text-white/60" : "text-[#5C6B68]"}`}>
                            {m.sender_name} · {fmtDate(m.created_at)}
                          </div>
                          <div className="mt-1 whitespace-pre-wrap">{m.body}</div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
                <form onSubmit={submitMessage} className="flex gap-2 border-t border-[#E0E4E2] p-3" data-testid="att-msg-form">
                  <input
                    type="text"
                    value={newMsg}
                    onChange={(e) => setNewMsg(e.target.value)}
                    placeholder={t("att.messages.placeholder")}
                    className="input-field flex-1"
                    data-testid="att-msg-input"
                  />
                  <button
                    type="submit"
                    disabled={posting || !newMsg.trim()}
                    className="btn-primary disabled:opacity-50"
                    data-testid="att-msg-submit"
                  >
                    <Send className="h-4 w-4" />
                  </button>
                </form>
              </div>
            </TabsContent>

            <TabsContent value="documents" className="mt-5">
              {documents.length === 0 ? (
                <div className="card-soft p-6 text-sm text-[#5C6B68]" data-testid="att-docs-empty">
                  {t("att.documents.empty")}
                </div>
              ) : (
                <ul className="divide-y divide-[#E0E4E2] rounded-xl border border-[#E0E4E2] bg-white" data-testid="att-docs-list">
                  {documents.map((d) => (
                    <li key={d.id} className="flex items-center justify-between gap-3 p-4">
                      <div className="min-w-0">
                        <div className="truncate font-display text-sm font-bold text-[#11322A]">
                          {d.original_filename}
                        </div>
                        <div className="text-[11px] text-[#5C6B68]">
                          {Math.round((d.size_bytes || 0) / 1024)} KB · {fmtDate(d.created_at)}
                        </div>
                      </div>
                      <a
                        href={d.download_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-secondary !py-1.5 text-xs"
                        data-testid={`att-doc-dl-${d.id}`}
                      >
                        {t("att.documents.download")}
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </TabsContent>

            <TabsContent value="history" className="mt-5">
              {history.length === 0 ? (
                <div className="card-soft p-6 text-sm text-[#5C6B68]" data-testid="att-hist-empty">
                  {t("att.history.empty")}
                </div>
              ) : (
                <ul className="space-y-3" data-testid="att-history-list">
                  {history.map((h) => (
                    <li key={h.id} className="card-soft flex items-center justify-between p-4">
                      <div>
                        <div className="text-xs font-bold uppercase tracking-wider text-[#E85D04]">
                          {t(`stage.${h.stage}`)}
                        </div>
                        <div className="text-[10px] text-[#5C6B68]">
                          {h.changed_by_name || "Attorney"}
                        </div>
                      </div>
                      <div className="text-xs text-[#0B1317]">{fmtDate(h.changed_at)}</div>
                    </li>
                  ))}
                </ul>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </main>

      <TreatmentPlanDialog
        open={showPlanDialog}
        onOpenChange={setShowPlanDialog}
        lead={lead}
        scope="attorney"
        onSaved={(updated) => {
          setLead(updated);
          setComplianceKey((k) => k + 1);
        }}
      />
    </div>
  );
}
